import { FC, useMemo } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { observer } from "mobx-react";

import RefreshIcon from "../../icons/RefreshIcon";
import CloseIcon from "../../icons/CloseIcon";

import Row from "../../ui/core/Row";
import CoreKeywordSearch from "../../ui/core/CoreKeywordSearch";
import CoreDropdown from "../../ui/core/CoreDropdown";
import { GradientButton } from "../../ui/core/GradientButton";
import PolicyQuestionsColumnVisibilityButton from "./PolicyQuestionsColumnVisibilityButton";

import { Payer } from "../../../services";
import { PreChatsHelper } from "../../helpers/preChatsHelper";
import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";
import { useAppStore } from "../../../hooks/useAppStore";
import { useOrgLinkBranding } from "../../../contexts/AuthContext";
import { ChatCategoryFilters } from "../../../stores/ChatCategoryStore";

interface PolicyQuestionsFiltersProps {
  payersData: Payer[];
  isLoadingPayers: boolean;
  onFormChange: (field: keyof ChatCategoryFilters, value: string) => void;
  panelMode?: boolean;
}

export const PolicyQuestionsFilters: FC<PolicyQuestionsFiltersProps> = observer(
  ({ payersData, onFormChange, isLoadingPayers, panelMode }) => {
    const {
      filters: formData,
      setFilters: setSharedFilters,
      setPolicyResearchFilters,
      fetchPolicyResearch,
      policyResearchColumnFilters,
      clearAllPolicyResearchColumnFilters,
      setIsAskingReveForPolicy,
      setIsPoliciesDocumentsDrawerOpen,
    } = useChatCategoryStore();
    const { setPolicyToPreview } = useAppStore();
    const { askReveLabel } = useOrgLinkBranding();

    // Get available states (converted to state names)
    const availableStates = useMemo(
      () => PreChatsHelper.extractAvailableStates(payersData),
      [payersData],
    );

    // Get selected state data
    const selectedState = useMemo(() => {
      return payersData.find(
        (state) =>
          state.name === formData.state || state.code === formData.state,
      );
    }, [payersData, formData.state]);

    // Get payer options for selected state (display names for dropdown, but store API name as value)
    // If no state is selected, show all unique payers from all states
    const payersOptions = useMemo(
      () =>
        PreChatsHelper.extractPayerOptions(
          formData?.state,
          selectedState,
          payersData,
        ),
      [selectedState, formData.state, payersData],
    );

    // The in-chat policy drawer (panelMode) shares the store's filters but must not
    // overwrite the policy research page's saved ones ("filters:policies").
    const setFilters = panelMode ? setSharedFilters : setPolicyResearchFilters;

    const onFilterChange = (filterName: string, value: string) => {
      setFilters({ ...formData, [filterName]: value });
      fetchPolicyResearch();
    };

    const onDropdownChange = (
      field: keyof ChatCategoryFilters,
      value: string,
    ) => {
      onFormChange(field, value);
    };

    const onRefresh = () => {
      fetchPolicyResearch();
    };

    const hasFiltersApplied = !!(
      formData.policySearchQuery ||
      formData.state ||
      formData.payer ||
      formData.lineOfBusiness ||
      formData.dateOfService ||
      Object.keys(policyResearchColumnFilters).length > 0
    );

    const onClearFilters = () => {
      setFilters({
        ...formData,
        policySearchQuery: "",
        state: "",
        payer: "",
        lineOfBusiness: "",
        dateOfService: "",
      });
      clearAllPolicyResearchColumnFilters();
      fetchPolicyResearch();
    };

    // Global (not policy-scoped) Ask Reve: same welcome + chat input flow as
    // PolicyQuestionsTable's "Ask about this policy", but with no policy selected, so
    // ChatStore.askQuestion sends the filters metadata without document_id/document_title.
    const onAskReve = () => {
      setPolicyToPreview(null);
      setIsPoliciesDocumentsDrawerOpen(true);
      setIsAskingReveForPolicy(true);
    };

    return (
      <Row
        sx={{
          justifyContent: "space-between",
          padding: {
            xs: "10px",
            sm: "10px",
            md: "10px",
            lg: "10px",
            xl: "12px",
          },
          height: "auto",
          gap: 1,
          flexWrap: "nowrap",
          alignItems: "center",
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
        }}
      >
        {panelMode ? null : (
          <IconButton
            aria-label="Refresh"
            onClick={onRefresh}
            sx={(theme) => ({
              border: "1px solid",
              borderRadius: "8px",
              width: 36,
              height: 36,
              flexShrink: 0,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            <RefreshIcon />
          </IconButton>
        )}

        <Box sx={{ minWidth: "200px", maxWidth: "350px", width: "100%" }}>
          <CoreKeywordSearch
            showSearchIcon
            disabled={isLoadingPayers}
            placeholder="Search policies..."
            value={formData.policySearchQuery || ""}
            onChange={(value) => onFilterChange("policySearchQuery", value)}
            sx={{ "& .MuiInputBase-root": { height: "36px" } }}
          />
        </Box>

        {hasFiltersApplied && !panelMode ? (
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={onClearFilters}
            sx={{
              minWidth: "auto",
              height: "36px",
              flexShrink: 0,
              color: "error.dark",
            }}
          >
            Clear Filters
          </Button>
        ) : null}

        <Box sx={{ flex: 1 }} />

        {panelMode ? null : (
          <GradientButton
            onClick={onAskReve}
            sx={{
              flexShrink: 0,
              // Org-configurable label (REV-2371) -- same sizing rules as AskReveActionMenu.
              minWidth: "110px",
              maxWidth: 220,
              height: 36,
              px: 2,
              boxShadow: 4,
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 500, color: "inherit" }}
            >
              {askReveLabel}
            </Typography>
          </GradientButton>
        )}

        <CoreDropdown
          label="State"
          disableOnNoOptions
          disabled={isLoadingPayers}
          loading={isLoadingPayers}
          selected={formData?.state}
          options={availableStates}
          onChange={(val: string) => onDropdownChange("state", val)}
        />

        <CoreDropdown
          label="Plan Name"
          disableOnNoOptions
          disabled={isLoadingPayers}
          loading={isLoadingPayers}
          selected={formData?.payer}
          options={payersOptions}
          onChange={(val: string) => onDropdownChange("payer", val)}
        />

        {panelMode ? null : <PolicyQuestionsColumnVisibilityButton />}
      </Row>
    );
  },
);
